/**
 * marketingIndustryKeywords — 시장 신호 수집 화면의 산업군별 예시 키워드 카탈로그.
 *
 * 주의: 라벨이 곧 검색어다. 수집 시 `t(labelKey)` 가 시드로 나가고, `kw` 는 화면 안에서만 쓰는 식별자다.
 * 주의: 라벨은 반드시 명사. 품목 매칭이 `ILIKE '%라벨 전체%'` 라서 "의류 소비" 같은 사건구는
 *   어떤 품목에도 안 걸린다(뉴스는 모이는데 영향 품목이 늘 빔). 세부 사건어는 백엔드 파생 확장에 맡긴다.
 * 채택 기준: 5년 뒤에도 통하는 산업 카테고리어, 6개 언어 보도에서 실제로 쓰이는 말, 최근 7일 기사 10건 이상.
 * 주의: 기사 건수만 보면 안 된다 — 그 언어 SCM 보도에 실제로 걸리는지(관련도)를 함께 본다.
 *   측정법: 언어별 Google 뉴스 RSS(hl/gl/ceid)에 `<라벨> when:30d` 를 던져 상위 30건 제목 중
 *   그 언어의 SCM 문맥어(공급·생산·재고·출하·가격·조달…) 비율을 세고, ko 같은 프리셋 점수를 기준선으로 비교.
 *   ja "バッテリー"는 100건이 잡히지만 야구·열사병 기사라 1/30 이었다(2026-09-07 실측 → 二次電池 로 교체).
 *   물류·환율·의류는 제조업 문맥어로 재면 구조적으로 낮게 나오니 카테고리별 문맥어를 따로 쓸 것.
 * LLM·API 를 부르지 않는다 — 예시 키워드는 매번 달라지면 안 된다.
 */

/** 산업군 미선택(전체) 상태 */
export const ALL_INDUSTRY = "__ALL__";

/**
 * 산업 무관 공통 키워드 — 가격·운임·환율·규제라는 횡단 메커니즘. 항상 노출된다.
 * 주석의 한국어는 이 항목이 원래 어떤 검색어였는지 남긴 것 — 값이 아니라 유래다.
 */
export const COMMON_KEYWORDS = [
  { kw: "RAW_MATERIAL", labelKey: "mktIntelPresetRawMaterial" },  // 원자재 가격 상승
  { kw: "OIL_PRICE",    labelKey: "mktIntelPresetOilPrice" },     // 유가
  { kw: "LOGISTICS",    labelKey: "mktIntelPresetLogistics" },    // 물류 운임 지연
  { kw: "FX",           labelKey: "mktIntelPresetFx" },           // 환율 달러 변동
  { kw: "TRADE_REG",    labelKey: "mktIntelPresetTradeReg" },     // 무역 규제 관세
];

// 배열 순서가 곧 칩 노출 순서.
// `kw` 는 언어 비의존 식별자 — 표시·검색은 labelKey 가 맡는다. DB·localStorage 어디에도 안 남고
// 리덕스 스토어(메모리)에서만 산다. 화면을 오갈 때 선택을 유지하는 용도라 값 자체에 의미는 없다.
// 배터리·반도체는 두 산업군에 겹쳐 둔다 — 같은 kw 를 쓰므로 "전체"에서 한 건으로 합쳐진다.
export const INDUSTRIES = [
  {
    code: "ELECTRONICS",
    labelKey: "mktIntelPresetElectronics",
    // umbrella(반도체/디스플레이/배터리) 뒤에 실품목에 직접 걸리는 구체어를 짝지어 둔다.
    keywords: [
      { kw: "SEMICONDUCTOR", labelKey: "mktIntelPresetSemiconductor" },  // 반도체 공급망
      { kw: "DRAM",          labelKey: "mktIntelPresetDram" },           // DRAM
      { kw: "DISPLAY",       labelKey: "mktIntelPresetDisplay" },        // 디스플레이 패널
      { kw: "OLED",          labelKey: "mktIntelPresetOled" },           // OLED
      { kw: "BATTERY",       labelKey: "mktIntelPresetBattery" },        // 배터리 소재 리튬
      { kw: "LITHIUM",       labelKey: "mktIntelPresetLithium" },        // 리튬
      { kw: "APPLIANCE",     labelKey: "mktIntelPresetAppliance" },      // 가전
    ],
  },
  {
    code: "AUTOMOTIVE",
    labelKey: "mktIntelPresetAutomotive",
    // 차량용 반도체(ADAS 카메라·CMOS·77GHz MMIC)가 실재해 SEMICONDUCTOR 를 재사용한다.
    keywords: [
      { kw: "AUTOMOTIVE",    labelKey: "mktIntelPresetAutomotive" },     // 자동차 부품 공급
      { kw: "EV",            labelKey: "mktIntelPresetEv" },             // 전기차
      { kw: "BATTERY",       labelKey: "mktIntelPresetBattery" },        // 배터리 소재 리튬
      { kw: "SEMICONDUCTOR", labelKey: "mktIntelPresetSemiconductor" },  // 반도체 공급망
    ],
  },
  {
    code: "CHEMICAL",
    labelKey: "mktIntelPresetChemical",
    keywords: [
      { kw: "PETROCHEM", labelKey: "mktIntelPresetPetrochem" },  // 석유화학
      { kw: "NAPHTHA",   labelKey: "mktIntelPresetNaphtha" },    // 나프타
      { kw: "PLASTICS",  labelKey: "mktIntelPresetPlastics" },   // 플라스틱
    ],
  },
  {
    code: "FOOD_BEV",
    labelKey: "mktIntelPresetFoodBev",
    // 최대 세그먼트. 국제 상품시황 보도량이 많고 실품목에 직접 걸리는 투입재를 채운다.
    keywords: [
      { kw: "GRAIN",      labelKey: "mktIntelPresetGrain" },      // 곡물
      { kw: "FLOUR",      labelKey: "mktIntelPresetFlour" },      // 밀가루
      { kw: "SUGAR",      labelKey: "mktIntelPresetSugar" },      // 설탕
      { kw: "PALM_OIL",   labelKey: "mktIntelPresetPalmOil" },    // 팜유
      { kw: "COCOA",      labelKey: "mktIntelPresetCocoa" },      // 코코아
      { kw: "MILK",       labelKey: "mktIntelPresetMilk" },       // 우유
      { kw: "PROC_FOOD",  labelKey: "mktIntelPresetProcFood" },   // 가공식품
      { kw: "FOOD_PRICE", labelKey: "mktIntelPresetFoodPrice" },  // 식품 물가
    ],
  },
  {
    code: "HEAVY_IND",
    labelKey: "mktIntelPresetHeavyInd",
    // 라벨을 "조선업"으로 하면 '%조선업%' 이 "조선기자재"에 안 걸린다. 동음이의 노이즈는 백엔드 파생어에서 흡수.
    keywords: [
      { kw: "SHIPBUILD",   labelKey: "mktIntelPresetShipbuild" },   // 조선
      { kw: "CONST_EQUIP", labelKey: "mktIntelPresetConstEquip" },  // 건설기계
      { kw: "POWER_EQUIP", labelKey: "mktIntelPresetPowerEquip" },  // 발전 설비
    ],
  },
  {
    code: "APPAREL",
    labelKey: "mktIntelPresetApparel",
    keywords: [
      { kw: "APPAREL",   labelKey: "mktIntelPresetApparel" },    // 의류
      { kw: "TEXTILE",   labelKey: "mktIntelPresetTextile" },    // 섬유
      { kw: "COTTON",    labelKey: "mktIntelPresetCotton" },     // 면화
      { kw: "POLYESTER", labelKey: "mktIntelPresetPolyester" },  // 폴리에스터
    ],
  },
];

const dedupeByKw = (list) => {
  const seen = new Set();
  return list.filter(e => e && !seen.has(e.kw) && seen.add(e.kw));
};

/** 산업군 코드 → 노출할 키워드. ALL_INDUSTRY 면 전 산업군을 이어붙인다(중복 제거). */
export function industryKeywords(code) {
  const targets = code === ALL_INDUSTRY || !code
    ? INDUSTRIES
    : INDUSTRIES.filter(i => i.code === code);
  return dedupeByKw(targets.flatMap(i => i.keywords));
}

/** kw → 카탈로그 항목(공통 + 전 산업군 횡단). 없으면 null — 직접 입력·이력 복원 값. */
export function keywordEntry(kw) {
  return COMMON_KEYWORDS.find(e => e.kw === kw)
    || INDUSTRIES.flatMap(i => i.keywords).find(e => e.kw === kw)
    || null;
}

/** 오래된 viewState 복원 시 가드 — 카탈로그에 있는 산업군 코드인지. */
export function isKnownIndustry(code) {
  return code === ALL_INDUSTRY || INDUSTRIES.some(i => i.code === code);
}
